/**
 * topSignal — distill a contact's triggered signals into the single most
 * actionable *planning move + deadline* to lead the Take Action panel with.
 *
 * This is the master-prompt's defensible edge: competitors show "high net
 * worth"; we surface the statutory move ("RSU cliff — reach out 8-10mo early")
 * with a real deadline. The move/deadline copy below is curated per signal
 * bucket from the statutory mechanics in `signalDefaults.ts` (kept here, not
 * derived from the free-text strategic-angle, so the hero copy is tight).
 *
 * Pure + dependency-free so it runs server-side (route) and could run client-side.
 */

/** Minimal shape of a triggered signal row (subset of ContactSignalGridItem). */
export interface TopSignalInput {
  bucket_key: string;
  /** Triggered iff value != null && value > 0. */
  value: number | null;
  reason?: string | null;
}

export interface TopSignal {
  /** Bucket the signal came from (for icon mapping in the UI). */
  bucketKey: string;
  /** Short human name, e.g. "RSU Cliff". */
  name: string;
  /** The planning move the advisor should lead with. */
  move: string;
  /** Statutory/timing deadline, when the bucket carries one. */
  deadline?: string;
  /** The engine's reason the signal fired (carried through for context). */
  reason?: string | null;
}

/**
 * Per-bucket lead copy. Priority = array order (earlier = higher priority);
 * the buckets with the hardest statutory deadlines lead. A bucket absent here
 * still counts as a (lower-priority) signal via DEFAULT_MOVE.
 */
const SIGNAL_PLAYS: ReadonlyArray<{
  bucketKey: string;
  name: string;
  move: string;
  deadline?: string;
}> = [
  {
    bucketKey: 'golden_handcuffs_457f',
    name: '457(f) Cliff',
    move: 'Plan around the deferred-comp cliff before the full balance is taxed at vest',
    deadline: 'Vests on a 3/5/7-yr cliff — engage well before',
  },
  {
    bucketKey: 'rsu_cliff',
    name: 'RSU Cliff',
    move: 'Tax-lot & diversification plan before the vest date',
    deadline: '~3 months — reach out 8-10 months early',
  },
  {
    bucketKey: 'rmd_cliff',
    name: 'RMD Shift',
    move: 'Roth conversion / QCD window before age-73 RMDs begin',
    deadline: 'Ages 71-72 are the final window',
  },
  {
    bucketKey: 'age_proxy_63_64',
    name: 'Medicare / IRMAA Window',
    move: 'Talk timelines before the Medicare enrollment & IRMAA window',
    deadline: 'Statutory enrollment deadline approaching',
  },
  {
    bucketKey: 'employment_gap',
    name: 'Low-Income Window',
    move: 'Roth conversion & gains harvesting while income is temporarily low',
    deadline: 'Only open until income restarts',
  },
  {
    bucketKey: 'job_change_rollover',
    name: '401(k) Rollover',
    move: 'Roll over and consolidate retirement accounts after the job change',
    deadline: 'Best handled in the first few months',
  },
  {
    bucketKey: 'senior_promotion',
    name: 'Comp Step-Up',
    move: 'Revisit the comp & equity plan after the promotion',
  },
  {
    bucketKey: 'retired',
    name: 'Recently Retired',
    move: 'Build the drawdown & tax sequencing plan for the new phase',
  },
  {
    bucketKey: 'long_tenure',
    name: 'NUA / Long Tenure',
    move: 'Net Unrealized Appreciation strategy on long-held employer stock',
  },
  {
    bucketKey: 'age_proxy_65',
    name: 'Retirement Transition',
    move: 'Start the retirement-income conversation',
  },
];

const PRIORITY = new Map(SIGNAL_PLAYS.map((p, i) => [p.bucketKey, i]));

const DEFAULT_MOVE = 'A money-in-motion signal worth a timely conversation';

function isTriggered(s: TopSignalInput): boolean {
  return s.value != null && s.value > 0;
}

/**
 * Pick the single highest-priority TRIGGERED signal and map it to a lead play.
 * Returns null when nothing is triggered (UI falls back to the cadence next-step).
 */
/**
 * Readable name for a bucket with no curated play: "founder_liquidity" →
 * "Founder liquidity". Unmapped buckets otherwise surfaced the raw DB key.
 */
export function humanizeBucketKey(bucketKey: string): string {
  const words = bucketKey.split(/[_\s]+/).filter((w) => w !== '');
  if (words.length === 0) return bucketKey;
  const sentence = words.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

export function buildTopSignal(signals: ReadonlyArray<TopSignalInput>): TopSignal | null {
  const triggered = signals.filter(isTriggered);
  if (triggered.length === 0) return null;

  // Rank: known plays by their PRIORITY order first, then unknown buckets, then
  // by signal value DESC as a tiebreaker.
  const rank = (s: TopSignalInput) => PRIORITY.get(s.bucket_key) ?? Number.MAX_SAFE_INTEGER;
  const best = [...triggered].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return (b.value ?? 0) - (a.value ?? 0);
  })[0]!;

  const play = SIGNAL_PLAYS.find((p) => p.bucketKey === best.bucket_key);
  return {
    bucketKey: best.bucket_key,
    name: play?.name ?? humanizeBucketKey(best.bucket_key),
    move: play?.move ?? DEFAULT_MOVE,
    deadline: play?.deadline,
    reason: best.reason ?? null,
  };
}
